import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, Flame, Layers, LifeBuoy, Scale, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BrandWordmark } from '../brand/BrandWordmark';
import {
  FOOTER_CHAPTERS_LINKS,
  FOOTER_MEMORIAL_LINKS,
  FOOTER_MINIMAL_LINKS,
  FOOTER_PRODUCT_LINKS,
  FOOTER_SUPPORT_LINKS,
  FOOTER_TRUST_BADGES,
  FOOTER_TRUST_LINKS,
  PUBLIC_DEMO_ROUTE,
} from '../../content/publicSiteContent';
import { StaffFooterLink } from './StaffFooterLink';
import '../../styles/public-footer.css';

const TRUST_BADGE_ICONS = {
  shield: Shield,
  download: Download,
  flame: Flame,
};

function FooterNavLink({ link, className }) {
  if (link.href) {
    return (
      <a href={link.href} className={className}>
        {link.label}
      </a>
    );
  }
  return (
    <Link to={link.to} className={className}>
      {link.label}
    </Link>
  );
}

function FooterLinkColumn({ title, links, linkClassName, className, icon: Icon }) {
  return (
    <div className={cn('public-footer__column', className)}>
      <p className="public-footer__column-title">
        {Icon ? <Icon className="public-footer__column-icon" strokeWidth={1.75} aria-hidden /> : null}
        {title}
      </p>
      <ul className="public-footer__link-list">
        {links.map((link) => (
          <li key={`${title}-${link.label}`}>
            <FooterNavLink link={link} className={linkClassName} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function PublicFooterPreCta({ onStartArchive, className }) {
  return (
    <div className={cn('public-footer__pre-cta', className)}>
      <div className="public-footer__pre-cta-inner">
        <h2 className="public-footer__pre-cta-title">Start building your family archive</h2>
        <p className="public-footer__pre-cta-copy">
          Begin with one person, one memory, or one photo.
        </p>
        <div className="public-footer__pre-cta-actions">
          {onStartArchive ? (
            <button type="button" onClick={onStartArchive} className="public-footer__btn-primary">
              Start your archive
              <ArrowRight className="h-4 w-4" aria-hidden />
            </button>
          ) : (
            <Link to="/register" className="public-footer__btn-primary">
              Start your archive
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          )}
          <Link to={PUBLIC_DEMO_ROUTE} className="public-footer__btn-secondary">
            View demo
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Public website footer — full, compact (memorial), or minimal (auth).
 * Not used on logged-in archive workspace pages.
 */
export function PublicFooter({
  variant = 'full',
  className = '',
  showPreFooterCta = false,
  onStartArchive,
}) {
  const year = new Date().getFullYear();
  const linkClass = 'public-footer__link';

  if (variant === 'minimal') {
    return (
      <footer
        className={cn('public-footer public-footer--minimal', className)}
        data-testid="public-minimal-footer"
      >
        <nav className="public-footer__minimal-nav" aria-label="Legal and support">
          {FOOTER_MINIMAL_LINKS.map((link, index) => (
            <React.Fragment key={link.label}>
              {index > 0 ? <span className="public-footer__divider-dot" aria-hidden>·</span> : null}
              <FooterNavLink link={link} className={linkClass} />
            </React.Fragment>
          ))}
        </nav>
        <div className="public-footer__staff-row">
          <StaffFooterLink variant="light" />
        </div>
        <p className="public-footer__minimal-copy">© {year} MyLegacyBridge</p>
      </footer>
    );
  }

  if (variant === 'compact') {
    return (
      <footer
        className={cn('public-footer public-footer--compact', className)}
        data-testid="public-compact-footer"
      >
        <div className="public-footer__shell">
          <div className="public-footer__compact-top">
            <Link to="/" className="public-footer__brand-link">
              <BrandWordmark variant="footerOnDark" className="h-6" />
            </Link>
            <nav className="public-footer__compact-nav" aria-label="Memorial page footer">
              {FOOTER_MEMORIAL_LINKS.map((link) => (
                <FooterNavLink key={link.label} link={link} className={linkClass} />
              ))}
            </nav>
          </div>
          <p className="public-footer__compact-tagline">
            Private by default. Shared only when you choose.
          </p>
          <div className="public-footer__staff-row">
            <StaffFooterLink />
          </div>
          <p className="public-footer__copyright">© {year} MyLegacyBridge. All rights reserved.</p>
        </div>
      </footer>
    );
  }

  return (
    <footer
      id="contact"
      className={cn('public-footer public-footer--full', className)}
      data-testid="public-site-footer"
    >
      {showPreFooterCta ? (
        <PublicFooterPreCta onStartArchive={onStartArchive} />
      ) : null}

      <div className="public-footer__main">
        <div className="public-footer__shell">
          <div className="public-footer__trust-badges" aria-label="Trust highlights">
            {FOOTER_TRUST_BADGES.map((badge) => {
              const Icon = TRUST_BADGE_ICONS[badge.icon] || Shield;
              return (
                <span key={badge.label} className="public-footer__trust-badge">
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                  {badge.label}
                </span>
              );
            })}
          </div>

          <div className="public-footer__grid">
            <div className="public-footer__brand">
              <div className="public-footer__brand-mark" aria-hidden>
                <span className="public-footer__brand-crest">G</span>
              </div>
              <Link to="/" className="public-footer__brand-link">
                <BrandWordmark variant="footerOnDark" className="h-7" />
              </Link>
              <p className="public-footer__brand-copy">
                Private family archives, stories, memorials and keepsakes.
              </p>
              <p className="public-footer__brand-trust">
                Private by default. Shared only when you choose.
              </p>
            </div>

            <FooterLinkColumn title="Product" links={FOOTER_PRODUCT_LINKS} linkClassName={linkClass} icon={Layers} />
            <FooterLinkColumn title="Trust" links={FOOTER_TRUST_LINKS} linkClassName={linkClass} icon={Scale} />
            <FooterLinkColumn title="Support" links={FOOTER_SUPPORT_LINKS} linkClassName={linkClass} icon={LifeBuoy} />
          </div>

          <div className="public-footer__chapters">
            <p className="public-footer__chapters-title">Built for every chapter</p>
            <div className="public-footer__chapters-links">
              {FOOTER_CHAPTERS_LINKS.map((link) => (
                <FooterNavLink key={link.label} link={link} className="public-footer__chapter-link" />
              ))}
            </div>
          </div>

          <div className="public-footer__bottom">
            <StaffFooterLink className="public-footer__staff-link" />
            <p className="public-footer__copyright">© {year} MyLegacyBridge. All rights reserved.</p>
            <p className="public-footer__tagline">Preserved with care — for the generations ahead.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default PublicFooter;
